import type { Feature } from 'ol';
import type { Geometry } from 'ol/geom';

import type { DrawToolType } from '@nora-monorepo/nora-api-client';

export type DrawToolOption
  = | 'fillColor'
    | 'fillOpacity'
    | 'iconCenter'
    | 'iconTint'
    | 'labelText'
    | 'lineColor'
    | 'lineOpacity'
    | 'lineWidth'
    | 'radiusLineVisible'
    | 'textAnchor';

export interface DrawToolOptions {
  drawToolType?: DrawToolType;
  fillColor?: string;
  fillOpacity?: number;
  font?: string;
  iconCenter?: number[];
  iconTint?: string;
  isEditing?: boolean;
  lineColor?: string;
  lineOpacity?: number;
  lineWidth?: number;
  labelText?: string;
  name?: string;
  nameVisible?: boolean;
  radiusLineVisible?: boolean;
  textAnchor?: string[];
}

export interface DrawTool {
  icon?: string;
  drawToolName: string;
  featureId?: string;
  featureName?: string;
  featureNameVisible?: boolean;
  options: DrawToolOptions;

  activate: () => void;
  deactivate: () => void;
  dismiss: () => void;

  save: (feature?: Feature<Geometry> | null) => Promise<void>;

  setFeatureName: (name: string) => void;
  setFeatureNameVisible: (visible: boolean) => void;
  getLabelPlaceholder: () => string;

  showFillColor: () => boolean;
  showLineColor: () => boolean;
  showLineOpacity: () => boolean;
  showLineWidth: () => boolean;
  showFillTab: () => boolean;
  showLineTab: () => boolean;
  showInfoTab: () => boolean;
  showStyles: () => boolean;
}

export interface UndoRedoFeatureAction {
  type: 'add' | 'remove' | 'modify';
  features: Feature<Geometry>[];
}

export enum DrawToolSaveMode {
  Save,
  Emit,
}

/**
 * Creates a new object composed of the specified keys from the source object.
 *
 * @template T - The type of the source object.
 * @template K - The keys to pick from the source object.
 * @param {T} object - The source object.
 * @param {K[]} keys - An array of keys to pick from the source object.
 * @returns {Pick<T, K>} A new object containing only the picked key-value pairs.
 */
function pick<T extends object, K extends keyof T>(object: T, keys: K[]): Pick<T, K> {
  const result: Partial<Pick<T, K>> = {};

  for (const key of keys) {
    if (Object.hasOwn(object, key)) {
      result[key] = object[key];
    }
  }

  return result as Pick<T, K>;
}

export function extractDrawToolOptions(
  feature: Feature<Geometry>,
): DrawToolOptions {
  const drawToolKeys: Array<keyof DrawToolOptions> = [
    'drawToolType',
    'fillColor',
    'fillOpacity',
    'font',
    'iconCenter',
    'iconTint',
    'isEditing',
    'labelText',
    'lineColor',
    'lineOpacity',
    'lineWidth',
    'name',
    'nameVisible',
    'radiusLineVisible',
    'textAnchor',
  ];

  const allProperties = feature.getProperties();
  const drawToolOptions = pick(allProperties, drawToolKeys);
  return drawToolOptions;
}
